import api from './api';

/**
 * The browser half of a call's media: the microphone and camera, a shared
 * screen, and the call configuration.
 *
 * Connections are no longer made here. Media goes through the LiveKit media
 * server (utils/callLiveKit.js); this file opens the devices whose tracks are
 * published there, and tells the UI how big a call may be.
 */

/* ------------------------------------------------------------------ *
 * Call configuration
 * ------------------------------------------------------------------ */

let configCache = null;
let configCachedAt = 0;

// Refetched now and then: the cap is server configuration, which can change
// while a tab stays open.
const CONFIG_CACHE_MS = 10 * 60 * 1000;

const FALLBACK_CONFIG = { maxParticipants: 20, callsAvailable: true };

/**
 * The participant cap and whether calls are available, from /api/calls/ice
 * (named for what it used to hold). A failed request falls back to defaults
 * and is not cached, so the next call asks again.
 */
export const getIceConfig = async () => {
    if (configCache && Date.now() - configCachedAt < CONFIG_CACHE_MS) return configCache;
    try {
        const res = await api.get('/calls/ice');
        configCache = res.data;
        configCachedAt = Date.now();
        return configCache;
    } catch {
        return configCache || FALLBACK_CONFIG;
    }
};

/* ------------------------------------------------------------------ *
 * Microphone
 * ------------------------------------------------------------------ */

/**
 * getUserMedia, with its errors turned into something a person can act on.
 *
 * The raw DOMException names are meaningless to a user and the distinctions
 * matter: "you declined" and "another app has the mic" need entirely
 * different responses, and showing "NotReadableError" for the second is how a
 * support ticket gets opened for a problem the user could have fixed in five
 * seconds by quitting Zoom.
 */
export const getLocalStream = async ({ video = false } = {}) => {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser cannot make calls. Try Chrome or Edge.');
    }

    // A call that just ended on Safari left its microphone open for a moment (see
    // stopStream), so this one needs no new permission prompt.
    const warmed = takeWarmMic();
    if (warmed && !video) {
        const stream = new MediaStream([warmed]);
        callStreams.add(stream);
        return stream;
    }
    if (warmed) warmed.stop();

    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            },
            video: video ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false
        });
        callStreams.add(stream);
        return stream;
    } catch (err) {
        const messages = {
            NotAllowedError: video
                ? 'Camera and microphone access was blocked. Allow it in the address bar and try again.'
                : 'Microphone access was blocked. Allow it in the address bar and try again.',
            PermissionDeniedError: 'Microphone access was blocked. Allow it in the address bar and try again.',
            NotFoundError: video ? 'No camera or microphone found.' : 'No microphone found.',
            DevicesNotFoundError: 'No microphone found.',
            NotReadableError: 'Your microphone is being used by another application.',
            TrackStartError: 'Your microphone is being used by another application.',
            OverconstrainedError: 'No device matched the requested settings.'
        };

        /*
         * getUserMedia requires a secure context. On http://<lan-ip>:3000 —
         * how this app is usually reached during development from a second
         * machine — navigator.mediaDevices is simply absent, and the generic
         * error above sends you hunting for a bug in the call code.
         */
        if (!window.isSecureContext) {
            throw new Error('Calls need a secure connection (HTTPS or localhost).');
        }

        throw new Error(messages[err.name] || `Could not open the microphone (${err.name}).`);
    }
};

/**
 * The screen, a window, or a tab.
 *
 * Constraints borrowed from hooks/useScreenRecorder.js, which solved this first
 * and for the same reason: uncapped, getDisplayMedia hands back the display's
 * native resolution, and on a high-density panel that is four times a 1080p
 * laptop's pixels going into an encoder that has to keep up with four other
 * peer connections at the same time.
 *
 * `audio: false` is deliberate and is the one real limitation of this first
 * version. Capturing system audio means mixing it into the outgoing microphone
 * track through a WebAudio graph — at which point "mute" stops being
 * `track.enabled = false` and becomes a gain node on a branch of a graph that
 * nothing else in the call code knows exists. Mute is the most safety-critical
 * control in a call and a mute that silently fails is the worst bug this feature
 * could have. The transceiver for screen audio is negotiated already (see
 * SLOTS), so adding it later is a replaceTrack and a second sink, not a
 * renegotiation.
 */
export const getDisplayStream = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('This browser cannot share a screen. Try Chrome or Edge.');
    }

    try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                width: { max: 1920 },
                height: { max: 1080 },
                frameRate: { ideal: 15, max: 30 }
            },
            audio: false
        });

        const [track] = stream.getVideoTracks();
        /*
         * 'detail' tells the encoder to protect sharpness over smoothness. A
         * shared screen is mostly text that does not move, and the default
         * setting spends the bitrate the other way round — which is how a
         * perfectly healthy share ends up with unreadable code on it.
         */
        if (track) track.contentHint = 'detail';
        return stream;
    } catch (err) {
        /*
         * Cancelling the picker is a decision, not a failure. It arrives as
         * NotAllowedError, exactly like a refused permission, and the caller
         * tells them apart by this flag rather than by matching on the message.
         */
        if (err.name === 'NotAllowedError') {
            const cancelled = new Error('Screen sharing was cancelled.');
            cancelled.cancelled = true;
            throw cancelled;
        }

        const messages = {
            NotFoundError: 'No screen was available to share.',
            NotReadableError: 'That screen could not be captured.',
            AbortError: 'Screen sharing stopped before it started.'
        };
        throw new Error(messages[err.name] || `Could not share the screen (${err.name}).`);
    }
};

/*
 * Safari (and every browser on an iPhone, which is Safari underneath) asks the
 * person for the microphone again whenever a new request follows a closed one.
 * So that back-to-back calls do not ask each time, a call's microphone is kept
 * open, silent, for a couple of minutes after the call - then let go. Other
 * browsers remember the permission themselves and stop at once, as before.
 */
const callStreams = new WeakSet();
const parked = new WeakSet();
const WARM_MIC_MS = 2 * 60 * 1000;
let warmMic = null;
let warmTimer = null;

const asksEveryTime = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const iOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    return iOS || (/Safari/i.test(ua) && !/Chrome|Chromium|Android|Edg|OPR|Firefox/i.test(ua));
};

const takeWarmMic = () => {
    clearTimeout(warmTimer);
    const track = warmMic;
    warmMic = null;
    if (!track || track.readyState !== 'live') return null;
    track.enabled = true;
    return track;
};

const keepMicWarm = (stream) => {
    const [mic] = stream.getAudioTracks().filter((t) => t.readyState === 'live');
    stream.getVideoTracks().forEach((t) => { try { t.stop(); } catch { /* gone */ } });
    if (!mic) return;
    if (warmMic && warmMic !== mic) { try { warmMic.stop(); } catch { /* gone */ } }
    clearTimeout(warmTimer);
    mic.enabled = false; // nothing is sent or recorded while it waits
    warmMic = mic;
    warmTimer = setTimeout(() => {
        try { mic.stop(); } catch { /* gone */ }
        if (warmMic === mic) warmMic = null;
    }, WARM_MIC_MS);
};

export const stopStream = (stream) => {
    if (!stream) return;
    if (parked.has(stream)) return; // the call's teardown can run twice
    if (callStreams.has(stream) && asksEveryTime()) {
        parked.add(stream);
        keepMicWarm(stream);
        return;
    }
    for (const track of stream.getTracks()) {
        try { track.stop(); } catch { /* already stopped */ }
    }
};

/** mm:ss for the in-call timer. */
export const formatDuration = (seconds) => {
    const s = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        return `${hours}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
};
